import React, { useState, useEffect } from 'react';
import { Key, X } from 'lucide-react';
import { PermissionsService, type Permission, type UpdatePermissionData } from '../../services/security/permissions.service';
import { NotificationToast } from '../ui/NotificationToast';

interface EditPermissionDrawerProps {
  isOpen: boolean;
  permission: Permission | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditPermissionDrawer: React.FC<EditPermissionDrawerProps> = ({
  isOpen,
  permission,
  onClose,
  onSuccess
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Estados del formulario
  const [formData, setFormData] = useState<UpdatePermissionData>({
    nombre: '',
    descripcion: ''
  });
  
  // Estados para errores
  const [errors, setErrors] = useState<Partial<UpdatePermissionData>>({});
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    if (isOpen && permission) {
      setIsVisible(true);
      // Cargar datos del permiso
      setFormData({
        nombre: permission.nombre,
        descripcion: permission.descripcion || ''
      });
      setErrors({});
    } else {
      setIsVisible(false);
    }
  }, [isOpen, permission]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleInputChange = (field: keyof UpdatePermissionData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<UpdatePermissionData> = {};
    
    // Validar nombre
    if (!formData.nombre?.trim()) {
      newErrors.nombre = 'El nombre del permiso es requerido';
    } else if (formData.nombre.trim().length < 2) {
      newErrors.nombre = 'El nombre debe tener al menos 2 caracteres';
    } else if (formData.nombre.trim().length > 100) {
      newErrors.nombre = 'El nombre no puede superar los 100 caracteres';
    }
    
    // Validar descripción (opcional)
    if (formData.descripcion && formData.descripcion.length > 255) {
      newErrors.descripcion = 'La descripción no puede superar los 255 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!permission || !validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const dataToSend: UpdatePermissionData = {};
      
      // Solo enviar campos que han cambiado
      if (formData.nombre?.trim() !== permission.nombre) {
        dataToSend.nombre = formData.nombre?.trim();
      }
      
      if ((formData.descripcion?.trim() || '') !== (permission.descripcion || '')) {
        dataToSend.descripcion = formData.descripcion?.trim() || '';
      }
      
      // Si no hay cambios, no hacer nada
      if (Object.keys(dataToSend).length === 0) {
        setNotificationConfig({
          title: 'Sin cambios',
          message: 'No se han detectado cambios en el permiso',
          type: 'info'
        });
        setShowNotification(true);
        setIsLoading(false);
        return;
      }
      
      await PermissionsService.updatePermission(permission.id, dataToSend);
      
      setNotificationConfig({
        title: '¡Permiso actualizado!',
        message: 'El permiso se ha actualizado exitosamente',
        type: 'success'
      });
      setShowNotification(true);
      
      // Esperar un momento para mostrar la notificación antes de cerrar
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1000);
      
    } catch (error: any) {
      console.error('Error updating permission:', error);
      
      setNotificationConfig({
        title: 'Error al actualizar permiso',
        message: error?.response?.data?.message || 'No se pudo actualizar el permiso. Inténtalo nuevamente.',
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !permission) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" style={{ top: '90px' }}>
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />
        
        <section className={`absolute right-0 top-0 h-full w-full max-w-md flex flex-col bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          
          {/* Header */}
          <div className="px-6 py-4 bg-blue-600 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-1 bg-blue-500 rounded-lg">
                  <Key className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Editar Permiso</h2>
                  <p className="text-blue-200 text-sm">{permission.nombre}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-blue-200 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col">
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
              <div className="flex-1 px-6 py-4 space-y-4 overflow-y-auto">
                
                {/* Nombre del permiso */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Permiso <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nombre || ''}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      errors.nombre 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-300 focus:border-blue-500'
                    }`}
                    placeholder="ej. ver, crear, editar, eliminar"
                    disabled={isLoading}
                    autoFocus
                  />
                  {errors.nombre && (
                    <p className="mt-1 text-sm text-red-600">{errors.nombre}</p>
                  )}
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción
                  </label>
                  <textarea
                    value={formData.descripcion || ''}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none ${
                      errors.descripcion 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-300 focus:border-blue-500'
                    }`}
                    placeholder="Describe qué permite hacer este permiso..."
                    disabled={isLoading}
                  />
                  {errors.descripcion && (
                    <p className="mt-1 text-sm text-red-600">{errors.descripcion}</p>
                  )}
                </div>

                {/* Información del sistema */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Información del sistema:</h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div><span className="font-medium">ID:</span> {permission.id}</div>
                    <div><span className="font-medium">Creado:</span> {new Date(permission.creadoEn).toLocaleDateString('es-ES')}</div>
                    <div><span className="font-medium">Estado:</span> {permission.activo ? 'Activo' : 'Inactivo'}</div>
                  </div>
                </div>

                {/* Información adicional */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Información importante:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Solo modifica los campos que necesites cambiar</li>
                    <li>• El nombre del permiso debe ser único en el sistema</li>
                    <li>• Los cambios afectarán a todos los módulos que usen este permiso</li>
                  </ul>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-400 transition-colors font-medium text-sm disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !formData.nombre?.trim()}
                    className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Guardando...</span>
                      </>
                    ) : (
                      <span>Guardar Cambios</span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </section>
      </div>

      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={4000}
        />
      )}
    </div>
  );
};